import { Link } from '@tanstack/react-router';
import { useState } from 'react';

import { MermaidDiagram } from '@/shared/ui/activity/mermaid-diagram';

import { isSafeDiagram, safeSourceUrl } from '../render-safety.service';

import { MioCodeBlock } from './mio-code-block';

import type { MioArtifact, MioSource } from '@mio/shared';

export function MioSources({ sources }: { sources: MioSource[] }) {
  if (!sources.length) return null;
  return (
    <ul className="text-fg-muted mt-3 space-y-1 text-[12px]" aria-label="Sources">
      {sources.map((source) => {
        const url = safeSourceUrl(source.url);
        return (
          <li key={source.id}>
            {url?.startsWith('/') ? (
              <Link to={url} className="underline underline-offset-2">
                {source.title}
              </Link>
            ) : url ? (
              <a
                href={url}
                target={url.startsWith('/') ? undefined : '_blank'}
                rel="noopener noreferrer"
                className="underline underline-offset-2"
              >
                {source.title}
              </a>
            ) : (
              source.title
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function MioArtifactView({
  artifact,
  hasLaterDiagram = false,
}: {
  artifact: MioArtifact;
  hasLaterDiagram?: boolean;
}) {
  const safe = artifact.type !== 'diagram' || isSafeDiagram(artifact.mermaid);
  const [diagramStatus, setDiagramStatus] = useState<'pending' | 'rendered' | 'failed'>('pending');
  const supersededFailure = hasLaterDiagram && (!safe || diagramStatus === 'failed');
  const diagram =
    artifact.type !== 'diagram' ? null : safe ? (
      <MermaidDiagram source={artifact.mermaid} onStatusChange={setDiagramStatus} />
    ) : (
      <p role="alert" data-diagram-status="failed" className="text-fg-muted text-xs">
        This diagram uses unsupported instructions. Ask Mio to redraw it.
      </p>
    );
  return (
    <section
      className={
        artifact.type === 'code'
          ? 'my-3 min-w-0'
          : 'border-line bg-surface-raised my-3 min-w-0 rounded-lg border p-3'
      }
      data-mio-artifact={artifact.id}
      aria-label={artifact.caption || 'Mio visual'}
    >
      {artifact.type === 'diagram' ? (
        <details open={!supersededFailure}>
          <summary hidden={!supersededFailure} className="text-fg-muted cursor-pointer text-xs">
            Earlier diagram failed; a later attempt appears below
          </summary>
          {diagram}
        </details>
      ) : null}
      {artifact.type === 'code' ? (
        <MioCodeBlock code={artifact.code} language={artifact.language} title={artifact.title} />
      ) : null}
      {artifact.type === 'table' ? (
        <div
          className="overflow-x-auto"
          role="region"
          aria-label={artifact.caption || 'Comparison table'}
          tabIndex={0}
        >
          <table className="w-full border-collapse text-left text-xs leading-5">
            <thead>
              <tr>
                {artifact.columns.map((column, index) => (
                  <th key={index} scope="col" className="border-line border-b p-2 font-medium">
                    {column}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {artifact.rows.map((row, index) => (
                <tr key={index}>
                  {artifact.columns.map((_, column) => (
                    <td key={column} className="border-line border-b p-2 align-top">
                      {row[column] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {artifact.caption ? (
        <p className="text-fg-muted mt-2 text-xs leading-5">{artifact.caption}</p>
      ) : null}
      <MioSources sources={artifact.sources} />
    </section>
  );
}
